import { ReactNode } from 'react';
import { Box, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import IosShareIcon from '@mui/icons-material/IosShare';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { Barcode, Field, Logo, PhoneFrame, RectoImage, SAMPLE, StatusBar, WalletCard, WalletPlatform } from './shared';

// Maquettes Google Wallet / Apple Wallet : couleurs littérales des systèmes, hors thème
const isLink = (type?: string) => type === 'website' || type === 'phone_number';

/* ---------- Notification ---------- */

export function NotificationScreen({ platform, card }: { platform: WalletPlatform; card: WalletCard }) {
  const ios = platform === 'ios';
  return (
    <PhoneFrame platform={platform} screenBg="#fff">
      <Box sx={{ p: 1 }}>
        <Box sx={{ display: 'flex', gap: 1, p: 1, bgcolor: ios ? '#f2f2f2' : '#eceff1', borderRadius: ios ? 2.5 : 1.5 }}>
          <Logo src={card.logo} size={26} />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.62rem', fontWeight: ios ? 600 : 700, lineHeight: 1.3 }}>{card.company}</Typography>
            <Typography sx={{ fontSize: '0.6rem', lineHeight: 1.3, wordBreak: 'break-word', color: card.message ? 'inherit' : '#adb5bd' }}>
              {card.message || 'Message de la notification'}
            </Typography>
          </Box>
        </Box>
      </Box>
    </PhoneFrame>
  );
}

/* ---------- Recto ---------- */

function AndroidRecto({ card }: { card: WalletCard }) {
  return (
    <PhoneFrame platform="android" screenBg="#202124">
      <StatusBar color="#fff" />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1, py: 0.75, color: '#fff' }}>
        <ArrowBackIcon sx={{ fontSize: 16 }} />
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <StarBorderIcon sx={{ fontSize: 16 }} />
          <MoreVertIcon sx={{ fontSize: 16 }} />
        </Box>
      </Box>
      <Box sx={{ mx: 0.5, bgcolor: '#fff', borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.75, borderBottom: '1px solid #e8eaed' }}>
          <Logo src={card.logo} size={22} />
          <Typography sx={{ fontSize: '0.55rem', fontWeight: 600 }}>{card.company}</Typography>
        </Box>
        <Box sx={{ px: 1, pt: 1 }}>
          <Typography sx={{ fontSize: '0.9rem', lineHeight: 1.25, textTransform: 'uppercase' }}>{card.programName}</Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
            <Field label="MES POINTS" value={SAMPLE.points} />
            <Field label={SAMPLE.label} value={SAMPLE.value} align="right" />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', my: 1.25 }}>
            <Barcode value={SAMPLE.cardNumber} />
            <Typography sx={{ fontSize: '0.45rem', mt: 0.25, letterSpacing: 0.5 }}>Carte n° : {SAMPLE.cardNumber}</Typography>
          </Box>
        </Box>
        <RectoImage card={card} height={78} />
      </Box>
    </PhoneFrame>
  );
}

function IosRecto({ card }: { card: WalletCard }) {
  return (
    <PhoneFrame platform="ios" screenBg="#000">
      <StatusBar color="#fff" />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1, py: 0.75 }}>
        <Box sx={{ width: 18, height: 18, borderRadius: '50%', bgcolor: '#3a3a3c', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CloseIcon sx={{ fontSize: 11, color: '#fff' }} />
        </Box>
        <Box sx={{ px: 0.75, height: 14, borderRadius: 2, bgcolor: '#3a3a3c', display: 'flex', alignItems: 'center' }}>
          <MoreHorizIcon sx={{ fontSize: 12, color: '#fff' }} />
        </Box>
      </Box>
      <Box sx={{ mx: 0.75, mt: 0.5, bgcolor: '#fff', borderRadius: 2, overflow: 'hidden', pb: 1.25 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', px: 1, py: 0.75 }}>
          <Logo src={card.logo} size={24} />
          <Box sx={{ textAlign: 'right' }}>
            <Typography sx={{ fontSize: '0.42rem', fontWeight: 700, color: '#444' }}>{SAMPLE.firstname}</Typography>
            <Typography sx={{ fontSize: '0.6rem', fontWeight: 600 }}>{SAMPLE.lastname}</Typography>
          </Box>
        </Box>
        <RectoImage card={card} height={80} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 1, mt: 0.75 }}>
          <Field label="MES POINTS" value={SAMPLE.points} />
          <Field label={SAMPLE.label} value={SAMPLE.value} align="right" />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 3 }}>
          <Barcode value={SAMPLE.cardNumber} height={36} />
          <Typography sx={{ fontSize: '0.45rem', mt: 0.25 }}>{SAMPLE.cardNumber}</Typography>
        </Box>
      </Box>
    </PhoneFrame>
  );
}

export function RectoScreen({ platform, card }: { platform: WalletPlatform; card: WalletCard }) {
  return platform === 'android' ? <AndroidRecto card={card} /> : <IosRecto card={card} />;
}

/* ---------- Verso ---------- */

function AndroidVerso({ card }: { card: WalletCard }) {
  const text = { fontSize: '0.58rem', lineHeight: 1.35, wordBreak: 'break-word' } as const;
  return (
    <PhoneFrame platform="android" screenBg="#fff">
      <StatusBar color="#202124" />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 1, py: 0.75, color: '#3c4043' }}>
        <ArrowBackIcon sx={{ fontSize: 16 }} />
        <FeedbackOutlinedIcon sx={{ fontSize: 16 }} />
      </Box>
      <Box sx={{ flex: 1, overflowY: 'auto', px: 1, pb: 1.5 }}>
        <Logo src={card.logo} size={22} />
        <Typography sx={{ fontSize: '0.95rem', lineHeight: 1.3, textTransform: 'uppercase', mt: 0.5, mb: 1.25 }}>{card.programName}</Typography>

        <Typography sx={{ ...text, fontWeight: 700 }}>Nom de membre</Typography>
        <Typography sx={{ ...text, mb: 1.25 }}>{SAMPLE.firstname} {SAMPLE.lastname}</Typography>

        {(card.title || card.message) && (
          <Box sx={{ mb: 1.25 }}>
            {card.title && <Typography sx={{ ...text, fontWeight: 700, mb: 0.5 }}>{card.title}</Typography>}
            {card.message && <Typography sx={text}>{card.message}</Typography>}
          </Box>
        )}

        {card.blocks.map((block, index) => (
          <Box key={block.id ?? index} sx={{ mb: 1.25 }}>
            {block.label && <Typography sx={{ ...text, fontWeight: 700 }}>{block.label}</Typography>}
            <Typography sx={{ ...text, color: isLink(block.type) ? '#1a73e8' : 'inherit' }}>{block.content}</Typography>
          </Box>
        ))}

        <Typography sx={{ ...text, color: '#5f6368', mb: 1.25 }}>Powered by Skoop</Typography>

        <Typography sx={{ ...text, fontWeight: 700 }}>Mon magasin</Typography>
        <Typography sx={{ ...text, mb: 1.25 }}>{SAMPLE.store}</Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, bgcolor: '#f1f3f4', borderRadius: 2, mb: 1 }}>
          <EditIcon sx={{ fontSize: 13 }} />
          <Typography sx={{ ...text, fontWeight: 500 }}>Ajouter un nom personnalisé</Typography>
        </Box>
        <Box sx={{ p: 1, bgcolor: '#f1f3f4', borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ ...text, color: '#3c4043' }}>Retrouver la carte de fidélité dans les services Google</Typography>
            <Toggle color="#1a73e8" />
          </Box>
          <Typography sx={{ ...text, color: '#3c4043', mt: 0.75 }}>
            Retrouvez votre nombre de points de fidélité et vos avantages dans Maps, Shopping, etc.
          </Typography>
        </Box>
      </Box>
    </PhoneFrame>
  );
}

function IosGroup({ children }: { children: ReactNode }) {
  return (
    <Box sx={{ bgcolor: '#fff', borderRadius: 2, px: 1, mb: 1, '& > *:not(:last-child)': { borderBottom: '1px solid #e5e5ea' } }}>
      {children}
    </Box>
  );
}

function IosVerso({ card }: { card: WalletCard }) {
  const text = { fontSize: '0.56rem', lineHeight: 1.35, wordBreak: 'break-word' } as const;
  const row = { py: 0.75 };
  return (
    <PhoneFrame platform="ios" screenBg="#f2f2f7">
      <StatusBar color="#000" />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 1, py: 0.75, color: '#007aff' }}>
        <ArrowBackIosNewIcon sx={{ fontSize: 13 }} />
        <IosShareIcon sx={{ fontSize: 14 }} />
      </Box>
      <Box sx={{ flex: 1, overflowY: 'auto', px: 1, pb: 1.5 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 1.25 }}>
          <Box sx={{ bgcolor: '#fff', borderRadius: 1, p: 0.5 }}>
            <Logo src={card.logo} size={36} />
          </Box>
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, textAlign: 'center', textTransform: 'uppercase', mt: 0.75, lineHeight: 1.3 }}>
            {card.programName}
          </Typography>
          <Typography sx={{ ...text, color: '#8e8e93' }}>Mise à jour : à l&apos;instant</Typography>
        </Box>

        <IosGroup>
          <Box sx={{ ...row, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography sx={text}>Mise à jour automatique</Typography>
            <Toggle color="#34c759" />
          </Box>
          <Box sx={{ ...row, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography sx={text}>Autoriser les notifications</Typography>
            <Toggle color="#34c759" />
          </Box>
        </IosGroup>

        <IosGroup>
          <Typography sx={{ ...text, ...row, color: '#ff3b30' }}>Supprimer la carte</Typography>
        </IosGroup>

        <IosGroup>
          {card.message && (
            <Box sx={row}>
              <Typography sx={text}>Dernière notification</Typography>
              <Typography sx={{ ...text, color: '#8e8e93' }}>{card.message}</Typography>
            </Box>
          )}
          <Box sx={{ ...row, display: 'flex', justifyContent: 'space-between' }}>
            <Typography sx={text}>Mon magasin</Typography>
            <Typography sx={{ ...text, color: '#8e8e93' }}>{SAMPLE.store}</Typography>
          </Box>
          {card.blocks.map((block, index) => (
            <Box key={block.id ?? index} sx={row}>
              {block.label && <Typography sx={text}>{block.label}</Typography>}
              <Typography sx={{ ...text, color: isLink(block.type) ? '#007aff' : '#8e8e93' }}>{block.content}</Typography>
            </Box>
          ))}
          <Typography sx={{ ...text, ...row, color: '#aeaeb2' }}>Powered by Skoop</Typography>
        </IosGroup>
      </Box>
    </PhoneFrame>
  );
}

function Toggle({ color }: { color: string }) {
  return (
    <Box sx={{ width: 24, height: 13, borderRadius: 7, bgcolor: color, position: 'relative', flexShrink: 0 }}>
      <Box sx={{ position: 'absolute', top: 2, right: 2, width: 9, height: 9, borderRadius: '50%', bgcolor: '#fff' }} />
    </Box>
  );
}

export function VersoScreen({ platform, card }: { platform: WalletPlatform; card: WalletCard }) {
  return platform === 'android' ? <AndroidVerso card={card} /> : <IosVerso card={card} />;
}
